import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import styles from './HeaderSearch.module.scss'
import SearchIcon from 'svg/search-icon.svg'
import classNames from 'classnames'
import ClearIcon from 'svg/SA/close-x-small.svg'
import useSearchProvider from 'components/SA/SearchProvider/hooks/useSearchProvider'
import { useSelector } from 'react-redux'
import { getSearchQuery } from 'selectors/searchProvider'

export type SearchInputPropsApi = {
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>
  triggerInputBlur: () => void
}

type SearchInputProps = {
  onSearch: (query: string) => void
  onFocusCallback?: (isFocused: boolean) => void
  onChangeCallback?: (query: string) => void
  shouldAutoFocus?: boolean
  dropdownRef: React.RefObject<HTMLDivElement | null>
  onCustomClearSearchCallback?: () => void
}

const ROTATING_PLACEHOLDER_WORDS = [
  'paintings',
  'abstracts',
  'figurative art',
  'landscapes',
  'wall sculpture',
  'artist name',
  'return policy',
  'anything',
]

const SearchInput = forwardRef<SearchInputPropsApi, React.PropsWithChildren<SearchInputProps>>(
  (
    {
      onSearch,
      onFocusCallback,
      onChangeCallback,
      shouldAutoFocus,
      dropdownRef,
      onCustomClearSearchCallback,
    },
    ref
  ) => {
    const [rotatingPlaceholderIndex, setRotatingPlaceholderIndex] = useState(0)
    const [isAnimating, setIsAnimating] = useState(true)
    const { getSearchProviderAttributes } = useSearchProvider()

    const [query, setQuery] = useState<string>('')
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const searchQuery = useSelector(getSearchQuery)

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value)
      onChangeCallback?.(event.target.value)
    }

    const handleSearch = () => {
      onSearch(query)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSearch()
      }
    }

    const handleClearInput = () => {
      setQuery('')
      onCustomClearSearchCallback?.()
      if (inputRef?.current) {
        inputRef.current.focus()
      }
    }

    const handleOnFocus = () => {
      setIsFocused(true)
      onFocusCallback?.(true)
    }

    const handleOnBlur = () => {
      setTimeout(() => {
        const dropdown = dropdownRef.current
        const activeElement = document.activeElement

        if (dropdown && activeElement && dropdown.contains(activeElement)) {
          // If the user clicked inside the dropdown (including on an icon), don't blur
          return
        }

        setIsFocused(false)
        onFocusCallback?.(false)
      }, 0)
    }

    const triggerInputBlur = () => {
      if (inputRef.current) {
        inputRef.current.blur()
      }
    }

    const totalItems = ROTATING_PLACEHOLDER_WORDS.length
    const rotatingWords = [...ROTATING_PLACEHOLDER_WORDS, ROTATING_PLACEHOLDER_WORDS[0]] // cloning first to infinite animation

    useImperativeHandle(ref, () => ({
      setIsFocused,
      triggerInputBlur,
    }))

    useEffect(() => {
      if (query !== searchQuery) {
        setQuery(searchQuery)
      }
    }, [searchQuery])

    useEffect(() => {
      if (shouldAutoFocus && inputRef.current) {
        inputRef.current.focus()
      }
    }, [shouldAutoFocus])

    useEffect(() => {
      if (isFocused) return

      const interval = setInterval(() => {
        setRotatingPlaceholderIndex((prev) => prev + 1)
      }, 2000)

      return () => clearInterval(interval)
    }, [isFocused])

    useEffect(() => {
      if (rotatingPlaceholderIndex === totalItems) {
        const timeout = setTimeout(() => {
          setIsAnimating(false)
          setRotatingPlaceholderIndex(0)

          // just to reset animation
          const timeout2 = setTimeout(() => {
            setIsAnimating(true)
          }, 50)

          return () => clearTimeout(timeout2)
        }, 500)

        return () => clearTimeout(timeout)
      }
    }, [rotatingPlaceholderIndex, totalItems])

    return (
      <div
        className={classNames(styles.searchField, {
          [styles.searchFieldFocused]: isFocused,
        })}
        data-type='search-input'
      >
        <div
          className={styles.searchIconBox}
          onClick={handleSearch}
          {...getSearchProviderAttributes([{ 'search-submit': true }])}
        >
          <SearchIcon />
        </div>

        <div
          className={styles.searchBoxWrapper}
          {...getSearchProviderAttributes([{ 'search-container': true }])}
        >
          <input
            ref={inputRef}
            type='text'
            name='search-input'
            autoComplete='off'
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            placeholder={isFocused ? 'Search' : ''}
            {...getSearchProviderAttributes([{ 'search-input': true }])}
          />
          {!isFocused && query?.length === 0 && (
            <div className={styles.placeholderBox}>
              <span className={styles.placeholderStatic}>Search for&nbsp;</span>
              <div className={styles.animatedWordBox}>
                <div
                  className={styles.animatedWordList}
                  style={{
                    transform: `translateY(-${rotatingPlaceholderIndex * 100}%)`,
                    transition: isAnimating ? 'transform 0.5s ease-in-out' : 'none',
                  }}
                >
                  {rotatingWords.map((word, index) => (
                    <span className={styles.animatedWord} key={index}>
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {query !== '' && (
          <div className={styles.clearInputBox} onClick={handleClearInput}>
            <ClearIcon />
          </div>
        )}
      </div>
    )
  }
)

export default SearchInput
